import { useState } from "react";
import { Pin, Trash2, Pencil, Play, AlertTriangle, Check, X, Youtube } from "lucide-react";
import { fileUrl } from "@/lib/adminApi";
import { ytThumb } from "@/lib/youtube";

const iconBtn = "rounded-full bg-black/55 p-1.5 text-white transition-colors duration-300";

export default function MediaCard({ m, themes, missing, onPin, onSave, onDelete }) {
  const [broken, setBroken] = useState(false);
  const [edit, setEdit] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [form, setForm] = useState({ theme: m.theme, caption: m.caption || "" });
  const lost = missing || broken;

  return (
    <div data-testid={`media-card-${m.id}`} className={`group overflow-hidden rounded-xl border bg-white ${m.pinned ? "border-[#C5A059] ring-2 ring-[#C5A059]/40" : "border-stone-200"}`}>
      <div className="relative aspect-square bg-stone-100">
        {lost ? (
          <div data-testid={`media-missing-${m.id}`} className="flex h-full flex-col items-center justify-center gap-1 text-center text-[11px] text-stone-400">
            <AlertTriangle className="h-5 w-5 text-[#C85A32]" /> Fichier manquant
          </div>
        ) : m.kind === "youtube" ? (
          <>
            <img src={ytThumb(m.youtube_id)} alt={m.caption || "Vidéo YouTube"} loading="lazy" className="h-full w-full object-cover" />
            <span className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-[#C85A32] px-2 py-0.5 text-[10px] font-semibold text-white">
              <Youtube className="h-3 w-3" /> YouTube
            </span>
          </>
        ) : m.kind === "video" ? (
          <>
            <video src={`${fileUrl(m.path)}#t=0.5`} muted preload="metadata" playsInline className="h-full w-full object-cover" />
            <Play className="pointer-events-none absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white" />
          </>
        ) : (
          <img src={fileUrl(m.path)} alt={m.caption || m.theme} loading="lazy" onError={() => setBroken(true)} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        )}
        <div className="absolute right-2 top-2 flex gap-1.5">
          <button data-testid={`media-pin-${m.id}`} onClick={onPin} className={`${iconBtn} ${m.pinned ? "!bg-[#C5A059] !text-[#1C1917]" : "hover:bg-[#C5A059]"}`} aria-label={m.pinned ? "Désépingler" : "Épingler"}><Pin className="h-3.5 w-3.5" /></button>
          <button data-testid={`media-edit-${m.id}`} onClick={() => { setForm({ theme: m.theme, caption: m.caption || "" }); setEdit(!edit); }} className={`${iconBtn} hover:bg-[#C5A059]`} aria-label="Modifier"><Pencil className="h-3.5 w-3.5" /></button>
          <button data-testid={`media-delete-${m.id}`} onClick={() => setConfirm(true)} className={`${iconBtn} hover:bg-[#C85A32]`} aria-label="Supprimer"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
        {confirm && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 p-3 text-center text-xs text-white">
            Supprimer définitivement ?
            <div className="flex gap-2">
              <button data-testid={`media-delete-confirm-${m.id}`} onClick={onDelete} className="rounded-full bg-[#C85A32] px-3 py-1 font-semibold">Supprimer</button>
              <button data-testid={`media-delete-cancel-${m.id}`} onClick={() => setConfirm(false)} className="rounded-full bg-white/20 px-3 py-1">Annuler</button>
            </div>
          </div>
        )}
      </div>
      {edit ? (
        <div className="space-y-2 p-2.5">
          <select data-testid={`media-edit-theme-${m.id}`} value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} className="w-full rounded-md border border-stone-200 px-2 py-1 text-xs focus:border-[#C5A059] focus:outline-none">
            {themes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input data-testid={`media-edit-caption-${m.id}`} value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} placeholder="Légende" maxLength={140} className="w-full rounded-md border border-stone-200 px-2 py-1 text-xs focus:border-[#C5A059] focus:outline-none" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setEdit(false)} aria-label="Annuler"><X className="h-4 w-4 text-stone-400" /></button>
            <button data-testid={`media-edit-save-${m.id}`} onClick={() => { onSave(form); setEdit(false); }} aria-label="Enregistrer"><Check className="h-4 w-4 text-emerald-600" /></button>
          </div>
        </div>
      ) : (
        <div className="p-2.5">
          <p className="truncate text-[11px] font-semibold uppercase tracking-[0.1em] text-[#8a6b2e]">{m.theme}</p>
          <p className="truncate text-xs text-stone-500">{m.caption || <span className="italic text-stone-300">Sans légende</span>}</p>
        </div>
      )}
    </div>
  );
}
